/**
 * BATCHES — which rows belonged to the run that just ended, so `exam_jobs` can be the record of what HAPPENED
 * while `examState` stays a snapshot of what is TRUE NOW.
 *
 * NO COLLECTION OF ITS OWN, and the reason is the field test above. A batch used to be a document in
 * `exam_batches` holding three things, and only one of them ever justified the collection:
 *
 *  1. THE OPEN KEY — "which wave is currently open for this step". That is a SNAPSHOT: one current value per
 *     step, replaced when the wave ends. It belongs on the exam, and a single-document compare-and-set mints it
 *     just as atomically as a unique partial index did. `openOrJoinBatch` below is that CAS.
 *  2. THE ANNOUNCEMENT LATCH — every row of a drained wave computes the same answer, so exactly one may act on
 *     it. Unsetting the key with the key itself in the filter is the same guarantee: the first writer matches,
 *     everybody else matches nothing. That is `claimBatchCompletion`.
 *  3. THE ASKS OF DISPATCHES WHOSE ROWS ALL DEDUPED. This was the load-bearing one, and it is now solved
 *     somewhere better: the enqueue pushes the trigger's cause onto the ROW it deduped onto, so the ask lives
 *     with the work rather than beside it. See `JobDoc.intents`.
 *
 * What made (3) safe to move is a change one layer down. While `grading`'s unit was a task across a submission
 * SET, intents were WORK INPUT — a row read them to discover whose answers to grade — so an intent landing on a
 * row that was later cancelled could lose a submission silently. The unit is one (task × submission) now, so a
 * row's scope is fixed at insert and intents are read only by the announcement. A lost one costs a cause in a
 * message, not an ungraded student, and no row's work depends on one arriving in time.
 *
 * SCOPED BY STEP, not by type or by trigger. `masterSolution` has three producing types sharing one
 * announcement, so they must share one batch; `grading` has three PHASES sharing one, so must they. That is the
 * same scope `stepTypesOf` defines for completion, which is what keeps the two aligned — see `batchScopeOf`.
 */

import { logger } from '@adornis/base/logging.js';
import { getCollection } from '@adornis/baseql/server/collections.js';
import { randomUUID } from 'node:crypto';
import { Exam } from '../../exam.js';
import type { JobDoc } from './job.js';

/**
 * One trigger's ASK, recorded on every row the dispatch produced — and pushed onto the row a deduped unit joined,
 * which is the whole reason it is a separate field rather than part of the payload (a payload is written once and
 * never modified; `ready()` reads it and `dedupeKey` is computed from it).
 *
 * NO SCOPE FIELDS, deliberately. This used to carry `taskRefs` and `submissionIds` because a row stood for a set
 * and could not say what it covered. A row now names exactly one submission and one main task, so the scope is
 * the payload and the outcome is `result` — leaving this to carry only what neither can express: WHY.
 */
export interface BatchIntent {
  /** Which trigger this was, in whatever vocabulary the step announces in (`RegradeCause` for grading). */
  cause?: string;
  /** Free-form extras a step's announcement needs — kept out of the payload so this stays a contract. */
  detail?: Record<string, unknown>;
}

/** Where a step's open key lives on the exam. Undecorated on `ExamState` on purpose — see `openOrJoinBatch`. */
const keyPath = (step: string): string => `examState.batchKeys.${step}`;

const exams = async () => getCollection(Exam as any);

/** How many times `openOrJoinBatch` may lose the mint and then find the winner's batch already closed. */
const MINT_ATTEMPTS = 5;

/**
 * The key every row of this dispatch will carry: the open batch for (examId, step), or a new one.
 *
 * THE MINT IS A COMPARE-AND-SET on the exam document, which is atomic without any index: the filter requires the
 * field to be absent (`null` matches missing), so of two concurrent dispatches exactly one writes and the other
 * matches nothing and reads the winner's key. That is the whole guarantee the unique partial index used to
 * provide, on a document that already exists.
 *
 * JOINING IS THE COMMON CASE, and it is why this is not "one batch per dispatch call". The canonical wave is one
 * upload changing a master solution, a grading scheme and the submissions at three different times — three
 * dispatches. If each minted its own key, the row that finishes last would belong to one of them and could
 * announce a third of what happened.
 *
 * NOT A DECORATED FIELD on `ExamState`. Every other field there is exposed through baseql because a client reads
 * it; no client reads this, and exposing a queue-internal key as part of the exam's public shape would invite one
 * to. Server code reads `examState` as a raw document, so an undecorated field is simply carried.
 */
export async function openOrJoinBatch(examId: string, step: string, attempt = 0): Promise<string> {
  const collection = await exams();
  const path = keyPath(step);
  const key = randomUUID();

  const minted = await collection.updateOne({ _id: examId, [path]: null } as any, { $set: { [path]: key } } as any);
  if (minted.modifiedCount > 0) {
    logger.info({ examId, step, batchKey: key }, 'Opened a batch');
    return key;
  }

  // Somebody else holds it. Read theirs — and if it closed in the meantime, mint again.
  const existing = await openBatchKey(examId, step);
  if (existing) return existing;

  // BOUNDED, even though each pass is expected to make progress: losing the mint and then finding the winner's
  // batch already closed means the two happened between our two round trips, which cannot keep happening. A
  // ceiling turns a pathological interleaving into a used key and a log line rather than a blown stack.
  if (attempt >= MINT_ATTEMPTS) {
    logger.warn({ examId, step, key }, 'Gave up joining a batch and used our own key');
    return key;
  }
  logger.info({ examId, step }, 'Batch closed while we were joining it — opening a new one');
  return openOrJoinBatch(examId, step, attempt + 1);
}

/** The key of the batch currently open for this step, or null. */
export async function openBatchKey(examId: string, step: string): Promise<string | null> {
  const collection = await exams();
  const exam = await collection.findOne({ _id: examId } as any, { projection: { 'examState.batchKeys': 1 } } as any);
  const key = (exam as any)?.examState?.batchKeys?.[step];
  return typeof key === 'string' && key.length > 0 ? key : null;
}

/**
 * The open batch's accumulated asks, for a caller that needs to know what this wave is already doing.
 *
 * READ THROUGH THE ROWS, which is the point of the redesign: the rows of a batch are the record of it, so this is
 * a lookup rather than a second store that has to be kept in step with them. Empty when no batch is open.
 */
export async function openBatchIntents(examId: string, step: string): Promise<BatchIntent[]> {
  const rows = await openBatchRows(examId, step);
  return rows.flatMap(row => (Array.isArray(row.intents) ? row.intents : []));
}

/**
 * Every row of the batch currently open for this step — the wave as it stands, mid-flight.
 *
 * For the callers that need a row's SCOPE and not just its cause: what a trigger asked for now lives in the
 * payload, since a row names one unit rather than standing for a set.
 */
export async function openBatchRows(examId: string, step: string): Promise<JobDoc[]> {
  const key = await openBatchKey(examId, step);
  if (!key) return [];
  const { jobsOfBatch } = await import('./job-store.js');
  return jobsOfBatch(key);
}

/**
 * Closes THIS row's batch AND claims the right to run its completion, in one atomic write. Returns true for the
 * one caller that won.
 *
 * BOTH IN ONE OPERATION, because they are the same moment and splitting them re-opens the race: every row of a
 * drained wave reaches this, all of them see the same drained queue, and exactly one may act. The key itself is
 * the guard — the first writer clears it and the rest match nothing, the same shape as `claimJob`'s
 * `status: 'pending'` filter and for the same reason.
 *
 * BY KEY, NOT BY STEP ALONE, and the difference is not hypothetical: matching "whatever is open for this step"
 * would let a row of a finished wave close a batch a newer dispatch had already opened, announcing the new wave's
 * rows against the old wave's completion.
 *
 * Losing is an ordinary outcome and means "somebody else is announcing this wave".
 */
export async function claimBatchCompletion(examId: string, step: string, batchKey: string): Promise<boolean> {
  if (!batchKey) return false;
  const collection = await exams();
  const path = keyPath(step);
  const result = await collection.updateOne(
    { _id: examId, [path]: batchKey } as any,
    { $unset: { [path]: '' } } as any,
  );
  return result.modifiedCount > 0;
}

/**
 * Closes a batch without claiming its completion — for a wave that ends with nothing to announce.
 *
 * Needed because a batch must not outlive its rows: an open key is what the next dispatch JOINS, so one left set
 * after its work finished would fold the next, unrelated pass into it. The types with no `onStepComplete`
 * (`structure`, `submissionSplit`) reach only this path.
 */
export async function closeBatchQuietly(examId: string, step: string, batchKey: string): Promise<void> {
  if (!batchKey) return;
  if (await claimBatchCompletion(examId, step, batchKey)) {
    logger.info({ examId, step, batchKey }, 'Closed a batch with nothing to announce');
  }
}

/** Batch keys of this exam that are still open — the prune guard's question, asked once per sweep. */
export async function openBatchKeys(examId: string): Promise<string[]> {
  const collection = await exams();
  const exam = await collection.findOne({ _id: examId } as any, { projection: { 'examState.batchKeys': 1 } } as any);
  const keys = (exam as any)?.examState?.batchKeys;
  if (!keys || typeof keys !== 'object') return [];
  return Object.values(keys as Record<string, unknown>).filter(
    (value): value is string => typeof value === 'string' && value.length > 0,
  );
}
